import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, GraduationCap, Globe2, Award, Compass, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Marquee from "@/components/Marquee";
import LeadForm from "@/components/LeadForm";
import UniversityCard from "@/components/UniversityCard";
import GuideCard from "@/components/GuideCard";
import FinalCTA from "@/components/FinalCTA";
import { MaskedLine, FadeUp, Overline } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { api, formatApiError } from "@/lib/api";
import { useSavedUniversities } from "@/lib/useSaved";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const HERO_IMG = "https://images.pexels.com/photos/34494497/pexels-photo-34494497.jpeg";

const WHY_CARDS = [
  { icon: GraduationCap, title: "World-Class Education", text: "Access globally recognised universities and qualifications.", span: "lg:col-span-3" },
  { icon: Award, title: "Scholarship Opportunities", text: "Explore university and international-student scholarships.", span: "lg:col-span-3" },
  { icon: Globe2, title: "Global Student Community", text: "Study alongside students from around the world.", span: "lg:col-span-2" },
  { icon: Compass, title: "Life Beyond University", text: "Experience Australia's cities, culture and lifestyle.", span: "lg:col-span-4" },
];

const JOURNEY = [
  { n: "01", title: "Explore", text: "Understand Australian universities, courses and opportunities." },
  { n: "02", title: "Compare", text: "Identify universities that match your academic and personal goals." },
  { n: "03", title: "Apply", text: "Understand applications, documentation and admission requirements." },
  { n: "04", title: "Prepare", text: "Navigate visas, accommodation and preparing for Australia." },
  { n: "05", title: "Arrive", text: "Access practical information about beginning student life in Australia." },
];

const TOOLKIT_ITEMS = [
  "University research worksheet",
  "Application & document checklist",
  "Scholarship tracker",
  "Pre-departure preparation timeline",
];

export default function Home() {
  const navigate = useNavigate();
  const { saved, toggle } = useSavedUniversities();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const [universities, setUniversities] = useState([]);
  const [guides, setGuides] = useState([]);
  const [toolkit, setToolkit] = useState({ name: "", email: "", consent: false });
  const [toolkitDone, setToolkitDone] = useState(false);
  const [toolkitLoading, setToolkitLoading] = useState(false);

  useEffect(() => {
    api.get("/universities").then((r) => setUniversities(r.data)).catch(() => {});
    api.get("/guides", { params: { limit: 3 } }).then((r) => setGuides(r.data)).catch(() => {});
  }, []);

  const scrollTo = (hash) => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });

  const submitToolkit = async (e) => {
    e.preventDefault();
    if (!toolkit.consent) {
      toast.error("Please agree to the privacy note before downloading.");
      return;
    }
    setToolkitLoading(true);
    try {
      await api.post("/toolkit", toolkit);
      setToolkitDone(true);
      toast.success("Your toolkit is unlocked below.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setToolkitLoading(false);
    }
  };

  return (
    <div data-testid="home-page">
      <SEO title="Vitaliah — Study in Australia, Clearly | Australian University Guidance" description="Clear, independent guidance for international students exploring Australian universities, Group of Eight, scholarships, applications and student life in Australia." />
      <Navbar />

      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative bg-navy text-cream overflow-hidden grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 pt-40 pb-24 lg:pt-48 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="text-xs font-semibold uppercase tracking-[0.3em] text-gold"
              >
                Independent Australian Study Guidance
              </motion.p>
              <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.04]">
                <MaskedLine delay={0.25}>Your Journey</MaskedLine>
                <MaskedLine delay={0.37}>to <span className="italic text-gold">Australia</span></MaskedLine>
                <MaskedLine delay={0.49}>Starts Here.</MaskedLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="mt-8 text-lg text-cream/75 max-w-xl leading-relaxed"
                data-testid="hero-subheading"
              >
                Clear, independent guidance for students exploring Australian universities.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.87, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="mt-4 text-sm text-cream/55 max-w-xl leading-relaxed"
              >
                Vitaliah helps prospective international students understand universities, scholarships, applications and student life in Australia — without the confusion.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="mt-10 flex flex-col sm:flex-row gap-4"
              >
                <button
                  data-testid="hero-cta-universities"
                  onClick={() => navigate("/universities")}
                  className="inline-flex items-center justify-center gap-3 bg-gold text-navy px-9 py-4 text-sm font-semibold tracking-wide hover:bg-cream transition-colors duration-300"
                >
                  Explore Universities <ArrowUpRight className="h-4 w-4" />
                </button>
                <button
                  data-testid="hero-cta-guidance"
                  onClick={() => scrollTo("#guidance")}
                  className="inline-flex items-center justify-center gap-3 border border-cream/30 text-cream px-9 py-4 text-sm font-semibold tracking-wide hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  Get Free Guidance
                </button>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 relative hidden lg:block"
            >
              <div className="absolute -top-6 -left-6 w-full h-full border border-gold/40" />
              <div className="relative overflow-hidden h-[480px]">
                <motion.img
                  src={HERO_IMG}
                  alt="Australian university architecture"
                  style={{ y: imgY }}
                  className="w-full h-[120%] object-cover saturate-[0.7]"
                />
                <div className="absolute inset-0 bg-navy/40" />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-cream text-navy px-6 py-4">
                <p className="text-xs uppercase tracking-[0.2em] text-navy/50">Focus</p>
                <p className="font-serif text-xl mt-1">Group of Eight & beyond</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* TRUST STATEMENT */}
      <section data-testid="trust-section" className="py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <FadeUp><Overline>Why Vitaliah exists</Overline></FadeUp>
              <FadeUp delay={0.1}>
                <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-medium tracking-tight leading-[1.1] text-navy">
                  Studying abroad is a big decision. <span className="italic text-gold">Understanding your options</span> shouldn't be difficult.
                </h2>
              </FadeUp>
            </div>
            <div className="lg:col-span-5 space-y-10">
              {[
                { n: "01", text: "Students navigating Australian study options face fragmented information — universities, courses, scholarships, applications, visas and living costs scattered across dozens of sources." },
                { n: "02", text: "Vitaliah brings this information together into one clear platform — structured, honest and written for students and parents, not salespeople." },
              ].map((c, i) => (
                <FadeUp key={c.n} delay={0.15 * i}>
                  <div className="flex gap-6 border-t border-border pt-8" data-testid={`manifesto-chapter-${c.n}`}>
                    <span className="font-serif text-3xl text-gold italic">{c.n}</span>
                    <p className="text-navy/70 leading-relaxed">{c.text}</p>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY AUSTRALIA */}
      <section data-testid="why-australia-section" className="py-28 bg-sand border-y border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <FadeUp><Overline>The destination</Overline></FadeUp>
              <FadeUp delay={0.1}>
                <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-medium tracking-tight text-navy">Why study in Australia?</h2>
              </FadeUp>
            </div>
            <FadeUp delay={0.2}>
              <button
                data-testid="why-australia-cta"
                onClick={() => navigate("/why-australia")}
                className="inline-flex items-center gap-3 border border-navy text-navy px-8 py-3.5 text-sm font-semibold tracking-wide hover:bg-navy hover:text-cream transition-colors duration-300"
              >
                Discover Why Australia <ArrowUpRight className="h-4 w-4" />
              </button>
            </FadeUp>
          </div>
          <div className="mt-16 grid lg:grid-cols-6 gap-6">
            {WHY_CARDS.map((c, i) => (
              <FadeUp key={c.title} delay={i * 0.08} className={c.span}>
                <div
                  data-testid={`why-card-${c.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group h-full bg-white border border-border p-9 hover:border-gold hover:-translate-y-1 hover:shadow-lg transition-[transform,border-color,box-shadow] duration-500"
                >
                  <c.icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-2xl font-medium text-navy">{c.title}</h3>
                  <p className="mt-3 text-sm text-navy/60 leading-relaxed">{c.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* GROUP OF EIGHT */}
      <section data-testid="go8-section" className="py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp><Overline>The Group of Eight</Overline></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-medium tracking-tight text-navy max-w-3xl">
              Explore Australia's Group of Eight Universities
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <p className="mt-6 text-navy/60 max-w-2xl leading-relaxed">
              Discover Australia's leading research-intensive universities and understand which one may suit your goals.
            </p>
          </FadeUp>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="go8-grid">
            {universities.map((u, i) => <UniversityCard key={u.id || u.slug} u={u} index={i} saved={saved.has(u.slug)} onToggleSave={toggle} />)}
          </div>
          <FadeUp delay={0.1} className="mt-14 text-center">
            <button
              data-testid="compare-universities-cta"
              onClick={() => navigate("/universities")}
              className="inline-flex items-center gap-3 bg-navy text-cream px-10 py-4 text-sm font-semibold tracking-wide border border-navy hover:bg-gold hover:border-gold hover:text-navy transition-colors duration-300"
            >
              Compare Universities <ArrowUpRight className="h-4 w-4" />
            </button>
          </FadeUp>
        </div>
      </section>

      {/* JOURNEY */}
      <section data-testid="journey-section" className="py-28 bg-navy text-cream relative grain">
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp><Overline dark>The process</Overline></FadeUp>
          <FadeUp delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-medium tracking-tight">Your Australia journey, simplified.</h2>
          </FadeUp>
          <div className="mt-16 grid md:grid-cols-5 gap-px bg-navy-border border border-navy-border">
            {JOURNEY.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.08} className="h-full">
                <div data-testid={`journey-stage-${s.n}`} className="h-full bg-navy p-8 hover:bg-navy-light transition-colors duration-500 group">
                  <span className="font-serif text-4xl italic text-gold/70 group-hover:text-gold transition-colors duration-500">{s.n}</span>
                  <h3 className="mt-6 font-serif text-2xl font-medium">{s.title}</h3>
                  <p className="mt-3 text-sm text-cream/60 leading-relaxed">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section id="guidance" data-testid="guidance-section" className="py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <FadeUp><Overline>Free guidance</Overline></FadeUp>
              <FadeUp delay={0.1}>
                <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-medium tracking-tight text-navy">Not sure where to start?</h2>
              </FadeUp>
              <FadeUp delay={0.2}>
                <p className="mt-6 text-navy/60 leading-relaxed max-w-md">
                  Tell us what you're looking for and we'll help you understand your Australian study options.
                </p>
                <div className="mt-10 space-y-4">
                  {["Independent, student-first advice", "No obligations, no pressure", "Responses from real advisors"].map((t) => (
                    <p key={t} className="flex items-center gap-3 text-sm text-navy/70">
                      <CheckCircle2 className="h-4 w-4 text-gold" /> {t}
                    </p>
                  ))}
                </div>
              </FadeUp>
            </div>
            <FadeUp delay={0.2} className="lg:col-span-7">
              <div className="bg-white border border-border p-8 lg:p-12">
                <LeadForm />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* GUIDES */}
      <section data-testid="guides-section" className="py-28 bg-sand border-y border-border">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <FadeUp><Overline>Knowledge base</Overline></FadeUp>
              <FadeUp delay={0.1}>
                <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-medium tracking-tight text-navy">Understand studying in Australia</h2>
              </FadeUp>
            </div>
            <FadeUp delay={0.2}>
              <button
                data-testid="explore-guides-cta"
                onClick={() => navigate("/guides")}
                className="inline-flex items-center gap-3 border border-navy text-navy px-8 py-3.5 text-sm font-semibold tracking-wide hover:bg-navy hover:text-cream transition-colors duration-300"
              >
                Explore All Guides <ArrowUpRight className="h-4 w-4" />
              </button>
            </FadeUp>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-6" data-testid="featured-guides-grid">
            {guides.map((g, i) => <GuideCard key={g.id || g.slug} g={g} index={i} />)}
          </div>
        </div>
      </section>

      {/* TOOLKIT */}
      <section id="toolkit" data-testid="toolkit-section" className="py-28 lg:py-36 bg-navy text-cream relative grain overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-[0.07] pointer-events-none hidden lg:block">
          <img src="https://images.pexels.com/photos/8199223/pexels-photo-8199223.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <FadeUp><Overline dark>Free resource</Overline></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-medium tracking-tight">The Australia Study Toolkit</h2>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-6 text-cream/70 leading-relaxed max-w-md">
                A practical checklist for students considering Australia, covering university research, applications, documentation, scholarships and preparation.
              </p>
            </FadeUp>
            {toolkitDone ? (
              <FadeUp className="mt-10 border border-gold/40 p-8" data-testid="toolkit-success">
                <p className="text-gold font-semibold text-sm uppercase tracking-[0.2em]">Unlocked</p>
                <ul className="mt-5 space-y-3">
                  {TOOLKIT_ITEMS.map((t) => (
                    <li key={t} className="flex items-center gap-3 text-sm text-cream/85">
                      <CheckCircle2 className="h-4 w-4 text-gold shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-xs text-cream/50">The full toolkit PDF is being finalised — we've noted your email and will send it the moment it's ready.</p>
              </FadeUp>
            ) : (
              <FadeUp delay={0.25}>
                <form data-testid="toolkit-form" onSubmit={submitToolkit} className="mt-10 space-y-5 max-w-md">
                  <Input data-testid="toolkit-name-input" required value={toolkit.name} onChange={(e) => setToolkit({ ...toolkit, name: e.target.value })} placeholder="Your name" className="rounded-none bg-navy-light border-navy-border text-cream placeholder:text-cream/40 px-4 py-6 focus-visible:ring-gold" />
                  <Input data-testid="toolkit-email-input" required type="email" value={toolkit.email} onChange={(e) => setToolkit({ ...toolkit, email: e.target.value })} placeholder="Your email" className="rounded-none bg-navy-light border-navy-border text-cream placeholder:text-cream/40 px-4 py-6 focus-visible:ring-gold" />
                  <div className="flex items-start gap-3">
                    <Checkbox id="tk-consent" data-testid="toolkit-consent-checkbox" checked={toolkit.consent} onCheckedChange={(v) => setToolkit({ ...toolkit, consent: v === true })} className="mt-0.5 rounded-none border-cream/40 data-[state=checked]:bg-gold data-[state=checked]:text-navy" />
                    <Label htmlFor="tk-consent" className="text-xs text-cream/60 leading-relaxed font-normal cursor-pointer">
                      I agree to receive the toolkit and occasional study-in-Australia guidance from Vitaliah. Unsubscribe anytime.
                    </Label>
                  </div>
                  <button
                    data-testid="toolkit-submit-button"
                    type="submit"
                    disabled={toolkitLoading}
                    className="inline-flex items-center gap-3 bg-gold text-navy px-9 py-4 text-sm font-semibold tracking-wide hover:bg-cream transition-colors duration-300 disabled:opacity-60"
                  >
                    {toolkitLoading ? "Unlocking..." : "Get the Free Toolkit"} <ArrowUpRight className="h-4 w-4" />
                  </button>
                </form>
              </FadeUp>
            )}
          </div>
          <FadeUp delay={0.2} className="hidden lg:block">
            <div className="border border-navy-border bg-navy-light/60 backdrop-blur p-10">
              <p className="text-xs uppercase tracking-[0.25em] text-gold">Inside the toolkit</p>
              <ul className="mt-8 space-y-6">
                {TOOLKIT_ITEMS.map((t, i) => (
                  <li key={t} className="flex items-start gap-5 border-b border-navy-border pb-6 last:border-0">
                    <span className="font-serif italic text-2xl text-gold/70">0{i + 1}</span>
                    <span className="text-cream/85 pt-1">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeUp>
        </div>
      </section>

      <FinalCTA />
      <Footer />
    </div>
  );
}
